import { useCallback, useEffect, useState } from 'react';
import { useCreatePath, useNotify, useRecordContext } from 'react-admin';
import { Link } from 'react-router-dom';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../../shared/clientTime';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useStillMounted } from '../../shared/recordScoped';
import { useFeature } from '../featureFlags/FeatureGate';
import { SectionCard } from '../../shared/Panel';
import { FEATURE_CS_LIGHT } from '../csWork/csWorkApi';
import { AvizationStateChip } from './AvizationQueue';
import { ClientUpdateCard } from './ClientUpdatesScreen';
import {
  AVIZATION_CHANNELS,
  AVIZATION_POINTS,
  FEATURE_CLIENT_AVIZATIONS,
  FEATURE_CLIENT_UPDATES,
  createAvization,
  fetchOrderAvizations,
  fetchOrderClientUpdates,
  toErrorMessage,
  type Avization,
  type ClientUpdate,
  type ContactOption,
} from './clientCommunicationApi';

const contactLabel = (c: ContactOption) =>
  `${c.source === 'order' ? 'kontakt zlecenia' : 'kartoteka'}: ${[c.name, c.phone, c.email].filter(Boolean).join(', ')}`;

/** Za godzinę, pełna minuta — w czasie polskim, jak pole formularza. */
const inAnHour = () => toDateTimeLocalValue(new Date(Date.now() + 3_600_000).toISOString()) ?? '';

/**
 * Awizacje przy zleceniu (sekcja 7 okna zlecenia; makieta: awizacje rekordu
 * z wynikiem, CS-08; prace 26.10.2026, pakiet P1, `clientAvizations`): lista
 * zadań z wynikiem i „Dodaj awizację” dla CS — punkt, kanał, termin, kontakt
 * z TMS albo kartoteki. Każdy zalogowany widzi; zakłada tylko CS.
 */
export const OrderAvizations = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const createPath = useCreatePath();
  const stillMounted = useStillMounted();
  const on = useFeature(FEATURE_CLIENT_AVIZATIONS);
  const id = record?.id;
  const [items, setItems] = useState<Avization[]>([]);
  const [canWrite, setCanWrite] = useState(false);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [adding, setAdding] = useState(false);
  const [point, setPoint] = useState('unloading');
  const [channel, setChannel] = useState('phone');
  const [dueAt, setDueAt] = useState(inAnHour);
  const [contactIdx, setContactIdx] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!on || id === undefined || id === null) return;
    fetchOrderAvizations(id)
      .then((r) => {
        if (!stillMounted()) return;
        setItems(r.items);
        setCanWrite(r.canWrite);
        setContacts(r.contacts);
      })
      .catch(() => undefined);
  }, [on, id, stillMounted]);
  useEffect(load, [load]);

  if (!on || id === undefined || id === null) return null;

  const save = async () => {
    setBusy(true);
    const c = contactIdx === '' ? null : contacts[Number(contactIdx)];
    try {
      await createAvization(id, {
        point,
        channel,
        dueAt: fromDateTimeLocalValue(dueAt) ?? dueAt,
        ...(c?.name ? { contactName: c.name } : {}),
        ...(c?.phone ? { contactPhone: c.phone } : {}),
        ...(c?.email ? { contactEmail: c.email } : {}),
        ...(note.trim() ? { note } : {}),
      });
      notify('Dodano awizację do kolejki', { type: 'success' });
      setAdding(false);
      setNote('');
      load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  return (
    <Box data-testid="order-avizations" sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        Awizacje
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak awizacji. Bez reguły awizacji klienta zadanie zakłada CS.
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((a) => (
            <li key={a.id} data-testid={`cs-zlecenie-awizacja-${a.id}`}>
              <Typography variant="body2" component="span">
                {/* Ekran awizacji jest dla CS (API 403 innym) — planista i Trade widzą sam wpis. */}
                {canWrite ? (
                  <Link to={createPath({ resource: 'client-avizations', type: 'show', id: a.id })}>Awizacja {a.id}</Link>
                ) : (
                  `Awizacja ${a.id}`
                )}{' '}
                ·{' '}
                {a.pointLabel} · {a.channelLabel} · {dateTimeNoSeconds(a.dueAt)}{' '}
              </Typography>
              <AvizationStateChip a={a} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {a.originLabel}
                {a.originNote ? ` (${a.originNote})` : ''}
                {a.lastResultLabel ? ` · wynik: ${a.lastResultLabel}` : ''}
                {a.note ? ` · ${a.note}` : ''}
                {a.skipReason ? ` · pominięta: ${a.skipReason}` : ''}
              </Typography>
            </li>
          ))}
        </Box>
      )}
      {canWrite ? (
        adding ? (
          <Stack spacing={1} sx={{ mt: 1 }} data-testid="order-avization-form">
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))' }}>
              <TextField
                select
                size="small"
                label="Punkt"
                value={point}
                onChange={(e) => setPoint(e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'order-avization-point' } }}
              >
                {AVIZATION_POINTS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Kanał"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'order-avization-channel' } }}
              >
                {AVIZATION_CHANNELS.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="datetime-local"
                label="Termin awizacji"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'order-avization-due' } }}
              />
            </Box>
            <TextField
              select
              size="small"
              label="Kontakt"
              value={contactIdx}
              onChange={(e) => setContactIdx(e.target.value)}
              helperText={contacts.length ? undefined : 'Zlecenie i kartoteka klienta nie mają kontaktu.'}
              slotProps={{ htmlInput: { 'data-testid': 'order-avization-contact' } }}
            >
              <MenuItem value="">bez kontaktu</MenuItem>
              {contacts.map((c, i) => (
                <MenuItem key={i} value={String(i)}>
                  {contactLabel(c)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Opis (opcjonalnie)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'order-avization-note' } }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" disabled={busy || dueAt === ''} onClick={() => void save()} data-testid="order-avization-save">
                Dodaj do kolejki
              </Button>
              <Button size="small" onClick={() => setAdding(false)}>
                Wróć
              </Button>
            </Box>
          </Stack>
        ) : (
          <Button size="small" sx={{ mt: 0.5 }} onClick={() => setAdding(true)} data-testid="order-avization-add">
            Dodaj awizację
          </Button>
        )
      ) : null}
    </Box>
  );
};

/**
 * Aktualizacje dla klienta przy zleceniu (sekcja 7; `clientUpdates`):
 * propozycje i wysłane — podgląd dla każdego, decyzja na liście
 * „Aktualizacje dla klienta” (CS).
 */
export const OrderClientUpdates = () => {
  const record = useRecordContext();
  const stillMounted = useStillMounted();
  const on = useFeature(FEATURE_CLIENT_UPDATES);
  const id = record?.id;
  const [items, setItems] = useState<ClientUpdate[]>([]);

  useEffect(() => {
    if (!on || id === undefined || id === null) return;
    fetchOrderClientUpdates(id)
      .then((r) => {
        if (stillMounted()) setItems(r);
      })
      .catch(() => undefined);
  }, [on, id, stillMounted]);

  if (!on || id === undefined || id === null || items.length === 0) return null;
  return (
    <Box data-testid="order-client-updates" sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        Aktualizacje dla klienta
      </Typography>
      <Stack spacing={1}>
        {items.map((u) => (
          <ClientUpdateCard
            key={u.id}
            u={u}
            canWrite={false}
            onChanged={(next) => setItems((list) => list.map((x) => (x.id === next.id ? next : x)))}
          />
        ))}
      </Stack>
    </Box>
  );
};

/** Sekcja 7: awizacje i aktualizacje (każda za swoim przełącznikiem). */
export const OrderClientCommunication = () => (
  <>
    <OrderAvizations />
    <OrderClientUpdates />
  </>
);

/**
 * Karta zlecenia w wersji bez okna zlecenia: przy „CS lekko” awizacje
 * i aktualizacje stoją w karcie „Komunikacja z klientem”; bez niego — we
 * własnej karcie, żeby funkcje nie zależały od siebie.
 */
export const OrderClientCommunicationCard = () => {
  const csLight = useFeature(FEATURE_CS_LIGHT);
  const avizations = useFeature(FEATURE_CLIENT_AVIZATIONS);
  const updates = useFeature(FEATURE_CLIENT_UPDATES);
  if (csLight || (!avizations && !updates)) return null;
  return (
    <SectionCard title="Awizacje i aktualizacje dla klienta">
      <OrderClientCommunication />
    </SectionCard>
  );
};
